<script setup>
import { inject, computed, ref} from 'vue';
import { useI18n } from 'vue-i18n';
import { ChevronLeft } from '@lucide/vue';
import useCartStore from '../store/cart';
import useOrderStore from '../store/order';
import { useCustomerStore } from '../store/customer';
import { useScheduleStore } from '../store/schedule';
import { getPrettyPrice, getCashPrice, getCardPrice, getCashDiscounted, uuidv4 } from '../shared/utils';
import { PAYMENT } from '../shared/constants';
import router from '../routes';
import ModalHead from './ModalHead.vue';
import ModalBtn from './ModalBtn.vue';
import ModalFooter from './ModalFooter.vue';

const rootData = inject('rootData');
const storagePrefix = inject('storagePrefix');

const timeStorageKey = `${storagePrefix}schedule/time`,
      dateStorageKey = `${storagePrefix}schedule/date`;
const restApi = inject('restApi');

const customerStore = useCustomerStore();
const scheduleStore = useScheduleStore();
const cartStore = useCartStore();
const orderStore = useOrderStore();
const { t } = useI18n();

const inProgress = ref(false);

const lastResponse = ref({});

// Idempotency key for the order request — generated once per attempt and
// reused on retry (double-click / network jitter) so the server replays the
// same order instead of creating a second one. Reset after a placed order.
const idempotencyKey = ref('');

const availableToContinue = computed(() => Boolean(useCartStore()?.count));

const nextStepLabel = computed(() => {
    if (!availableToContinue?.value) return t('cart.empty');
    return t('payment.confirm');
})

const handleOrder = function(values) {
        inProgress.value = true;

        // Keep the same key across retries; only mint a new one for a fresh attempt.
        if (!idempotencyKey.value) {
            idempotencyKey.value = uuidv4();
        }

        const orderCart = {
            "lineItems": Object.values(cartStore.contents),
            "note": String(customerStore.instructions ? customerStore.instructions : ''),
            "orderType": {
                "id":   rootData.services[customerStore.orderType].cid,
                "label": rootData.services[customerStore.orderType].name
            },
            "merchant": {
                "id": rootData.merchantUuid
            }
        }

        // make API request
        restApi
            .post('clover/add-order', {
                orderCart,                  // already fit for clover
                message: '',                // honeypot
                customerId: customerStore.customerId,
                orderTypeId: rootData.services[customerStore.orderType].id,
                orderType: customerStore.orderType,
                orderTypeParams: {
                    day: scheduleStore.formattedSelectedDate,
                    time: scheduleStore.formattedSelectedTime,
                    date: scheduleStore.isoSelectedDate,    // 'YYYY-MM-DD' in merchant TZ, null for ASAP
                    timeIso: scheduleStore.isoSelectedTime, // 'HH:MM' (24h), null for ASAP
                    address: String(customerStore.isDeliveryOrder ? `${customerStore?.street}, ${customerStore?.city}, ${customerStore?.state}, ${customerStore?.zip}` : ''),
                    uberQuoteId: customerStore.uberQuote?.quoteId || null,
                    // Dropoff contact for courier delivery (the Default flow's
                    // order step has no customer param server-side).
                    dropoff: customerStore.isDeliveryOrder ? {
                        firstName: customerStore.firstName,
                        lastName: customerStore.lastName,
                        phoneNumber: customerStore.phoneNumber,
                        street: customerStore.street,
                        city: customerStore.city,
                        state: customerStore.state,
                        zip: customerStore.zip,
                    } : null,
                },
                paymentOption: customerStore.paymentOption,
                idempotencyKey: idempotencyKey.value,
            })
            .then(response => {
                const orderId = response?.data?.data?.order_id;
                if ( (response.status >= 200 && response.status < 300) && orderId ) {
                    orderStore.orderId = orderId;
                    idempotencyKey.value = ''; // order placed — next attempt gets a fresh key
                    router.push('/thank-you/')
                }
            })
            .catch(error => {
                console.warn('Catch:', error)
                inProgress.value = false
            })
            .finally(() => {
                inProgress.value = false;
            })
    }

</script>

<template>
    <ModalHead>
        <template #additional>
            <router-link to="/customer/">
                <ChevronLeft/>
            </router-link>
        </template>
    </ModalHead>
    <div class="modal__body">
        <div class="paymentOptionHead">{{ t('payment.selectOption') }}</div>
        <div class="paymentOption">
            <div class="paymentOptionInner">
                <label for="paymentOption1">
                    <input id="paymentOption1" name="paymentOption" type="radio" v-model="customerStore.paymentOption" v-bind="customerStore.paymentOptionProps" :value="PAYMENT.CARD"/>
                    <div class="paymentOptionContent">
                        <div class="paymentOptionName">{{ getPrettyPrice( getCardPrice(cartStore.checkoutTotal) ) }}</div>
                        <div class="paymentOptionInfo">{{ t('payment.card') }}</div>
                    </div>
                </label>
                <label for="paymentOption2">
                    <input id="paymentOption2" name="paymentOption" type="radio" v-model="customerStore.paymentOption" v-bind="customerStore.paymentOptionProps" :value="PAYMENT.CASH"/>
                    <div class="paymentOptionContent">
                        <div class="paymentOptionName">{{ getPrettyPrice (getCashPrice(cartStore.checkoutTotal) ) }}</div>
                        <div class="paymentOptionInfo">{{ t('payment.cash') }}</div>
                    </div>
                </label>
            </div>
        </div>
    </div>
    <ModalFooter>      
        <template v-if="availableToContinue">
            <ModalBtn class="btn-primary btn-action" @click="handleOrder" :data-processing="inProgress || null">
                {{ nextStepLabel }}
            </ModalBtn>
        </template>
        <template v-else>
            <ModalBtn class="btn-inactive" disabled>{{ nextStepLabel }}</ModalBtn>
        </template>
    </ModalFooter>
</template>

<style lang="scss" scoped>
.paymentOption,
.paymentTime {
    width: 100%;
    position: relative;
    margin-bottom: 1.25rem;
    border-radius: var(--rounded-full);
    overflow: hidden;
    color: #333333;
    border: 2px solid #333333;

    &Head {
        font-weight: 600;
        margin-bottom: 0.75rem;
        font-size: 0.875rem;
        text-align: center;
    }

    &Inner {
        display: flex;
        justify-content: space-between;
        width: 100%;
        position: relative;
        flex-wrap: wrap;

        label {
            cursor: pointer;
            -webkit-appearance: button;
            appearance: button;
            position: relative;
            user-select: none;
            overflow: hidden;
            flex: 1 0 50%;
            border-radius: var(--rounded-full);
        }
    }

    &Info {
        font-weight: 400;
        font-size: 0.875em;
        margin-top: 0.125em
    }
    &Content {
        display: block;
        text-align: center;
        font-weight: 700;
        background-color: #fff;
        padding: 0.5rem 0.75rem;
        font-size: 1rem;
        border-radius: 9999px;
        border: 2px solid transparent;
        line-height: 1;

        label:last-child & {
            margin: -2px -2px -2px 0;
        }
        label:first-child & {
            margin: -2px 0 -2px -2px;
        }
    }

    input[type="radio"] {
        opacity: 0.01;
        position: absolute;
        width: 100%;
        height: 100%;
        padding: 0;
        margin: 0;
        opacity: 0;
        cursor: pointer;
        z-index: 3;

        &:checked + .paymentOptionContent {
            background-color: var(--c-primary);
            // color: #fff;
            border-color: #333333;
            
            .paymentOptionName {
                font-weight: 900;
            }
        }
    }
}
.modal {
    &__body {
        padding: 16px;
        // min-height: 80vh;
        // min-height: calc(100vh - 170px);
        // min-height: calc(100svh - 170px); // 146
        
        @media (min-width: 576px) {
            min-height: auto;
        }
    }
}
</style>